/**
 * TaskAutomationModal - Modal de visualização do Kanban de Automações para Tarefas
 *
 * Reusa AutomationKanbanColumn/AutomationCard (CRM) e segue o mesmo padrão visual
 * de AutomationKanbanModal (Negócios) e ConversationAutomationModal (Conversas).
 *
 * RELEASE: 1.0.0 - 2026-06-01 - Versão inicial
 */

import { useNavigate } from 'react-router-dom';
import { Zap, Loader2, LayoutGrid } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { AutomationKanbanColumn } from '@/components/crm/deals/AutomationKanbanColumn';
import { useTaskPhaseAutomations } from '@/hooks/useTaskPhaseAutomations';

// =========================================================================
// Tipos de fase aceitos (compatível com TaskPhaseCount adaptado pelo board)
// =========================================================================
export interface TaskAutomationPhase {
  id: string;
  name: string;
  color: string;
}

interface TaskAutomationModalProps {
  /** Controla se o modal está aberto */
  open: boolean;
  /** Callback para alterar estado do modal */
  onOpenChange: (open: boolean) => void;
  /** ID da categoria de tarefas */
  categoryId: string | null;
  /** Nome da categoria (exibido no header) */
  categoryName: string;
  /** Fases da categoria já carregadas pelo board (sem novo SELECT) */
  phases: TaskAutomationPhase[];
}

/**
 * Modal que exibe o Kanban de Automações para Tarefas
 */
export function TaskAutomationModal({
  open,
  onOpenChange,
  categoryId,
  categoryName,
  phases,
}: TaskAutomationModalProps) {
  const navigate = useNavigate();

  const {
    getTriggerAutomations,
    getActionAutomations,
    totalAutomations,
    isLoading,
  } = useTaskPhaseAutomations(categoryId);

  // ---------- Handlers de navegação ----------

  /** Navega para criação de novo fluxo, repassando contexto da fase */
  const handleCreateFlow = (phaseId: string) => {
    navigate(
      `/tenant/flows?new=true&preset_task_phase=${phaseId}&task_category=${categoryId}`,
    );
    onOpenChange(false);
  };

  /** Navega para edição de um fluxo existente */
  const handleEditFlow = (flowId: string) => {
    navigate(`/tenant/flows/${flowId}`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[95vw] h-[85vh] p-0 gap-0">
        {/* Header fixo */}
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10">
              <Zap className="h-5 w-5 text-amber-500" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-lg font-semibold">
                Regras de Automação e Gatilhos
              </DialogTitle>
              <DialogDescription className="flex items-center gap-2 mt-0.5">
                <LayoutGrid className="h-3.5 w-3.5" />
                Categoria: {categoryName}
                {totalAutomations > 0 && (
                  <Badge variant="secondary" className="ml-2 h-5 text-[10px]">
                    {totalAutomations}{' '}
                    {totalAutomations === 1 ? 'automação' : 'automações'}
                  </Badge>
                )}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Conteúdo */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3 text-muted-foreground">
                <Loader2 className="h-8 w-8 animate-spin" />
                <span className="text-sm">Carregando automações...</span>
              </div>
            </div>
          ) : phases.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <LayoutGrid className="h-12 w-12 mb-3 opacity-30" />
              <p className="text-sm">Nenhuma fase encontrada nesta categoria</p>
            </div>
          ) : (
            <ScrollArea className="h-full">
              <div className="flex gap-4 p-6 h-[calc(85vh-100px)]">
                {phases.map((phase) => (
                  <AutomationKanbanColumn
                    key={phase.id}
                    phaseId={phase.id}
                    phaseName={phase.name}
                    phaseColor={phase.color}
                    triggerAutomations={getTriggerAutomations(phase.id)}
                    actionAutomations={getActionAutomations(phase.id)}
                    onCreateFlow={handleCreateFlow}
                    onEditFlow={handleEditFlow}
                  />
                ))}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
